/**
 * Evaluations API client
 * Provides a simple function to persist an evaluation payload to the backend.
 */

/**
 * Saves an evaluation payload to the backend.
 * The backend is expected to be an Azure Function reachable at `/api/save-evaluation`.
 *
 * @param {Object} payload - The JSON payload to persist. Should include `user_config` and related fields.
 * @returns {Promise<Object>} - The JSON response from the backend.
 */
export async function saveEvaluation(payload) {
  const response = await fetch('/api/evaluation', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(text || `Failed to save evaluation (HTTP ${response.status})`);
  }

  return response.json();
}

/**
 * Retrieves stored evaluations (test cases) from the backend.
 * Accepts optional filters aligned with UI controls.
 *
 * @param {Object} [filters]
 * @param {string} [filters.state]
 * @param {('anonymous'|'signed_in')} [filters.auth]
 * @param {string} [filters.preset]
 * @param {number} [filters.limit=100]
 * @returns {Promise<Array<Object>>}
 */
export async function listEvaluations(filters = {}) {
  const params = new URLSearchParams();
  if (filters.state) params.set('state', filters.state);
  if (filters.auth) params.set('auth', filters.auth);
  if (filters.preset) params.set('preset', filters.preset);
  params.set('limit', String(filters.limit || 100));

  const query = params.toString();
  const url = query ? `/api/evaluations?${query}` : '/api/evaluations';

  const response = await fetch(url, { method: 'GET', headers: { 'Accept': 'application/json' } });
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(text || `Failed to fetch evaluations (HTTP ${response.status})`);
  }
  return response.json();
}


/**
 * Lists evaluation runs from the orchestrator (Function App).
 * @param {number} [limit=50]
 * @returns {Promise<Array<Object>>}
 */
export async function listEvalRuns(limit = 50) {
  const url = `/api/eval-runs?limit=${encodeURIComponent(String(limit))}`;
  const response = await fetch(url, { method: 'GET', headers: { Accept: 'application/json' } });
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(text || `Failed to list eval runs (HTTP ${response.status})`);
  }
  return response.json();
}

/**
 * Lists available evaluators from the backend/runner registry.
 * Returns an object map: { [evaluatorId]: { title?: string, description?: string, ... } }
 */
export async function listEvaluators() {
  try {
    const response = await fetch('/api/evaluators', { method: 'GET', headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const json = await response.json();
    // Accept either array or object; normalize to object map
    if (Array.isArray(json)) {
      const map = {};
      for (const item of json) {
        const id = item?.id || item?.name || item?.key;
        if (id) map[id] = item;
      }
      return map;
    }
    return json || {};
  } catch (e) {
    console.warn('listEvaluators failed; returning empty object', e);
    return {};
  }
}

/**
 * Gets a single evaluation run by id from the orchestrator.
 * @param {string} id
 * @returns {Promise<Object>}
 */
export async function getEvalRun(id, opts = {}) {
  const { signal } = opts || {};
  const response = await fetch(`/api/eval-runs/${encodeURIComponent(id)}`, { method: 'GET', headers: { Accept: 'application/json' }, signal });
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(text || `Failed to get eval run ${id} (HTTP ${response.status})`);
  }
  return response.json();
}

/**
 * Triggers an evaluation run via the orchestrator.
 * Supports explicit IDs (preferred) and/or broad filters for auditing.
 * If `options.ids` is provided, the backend will run ONLY those IDs.
 *
 * @param {Object} filters - { state?, auth?, preset?, limit? }
 * @param {Object} options - { environment?, concurrency?, notes?, ids?: string[] }
 * @returns {Promise<{id: string, status: string, location: string}>}
 */
export async function createEvalRun(filters = {}, options = {}) {
  // Build query string from supported filters
  const params = new URLSearchParams();
  if (filters.state) params.set('state', filters.state);
  if (filters.auth) params.set('auth', filters.auth);
  if (filters.preset) params.set('preset', filters.preset);
  if (Number.isFinite(filters.limit)) params.set('limit', String(filters.limit));

  // Allow explicit IDs; include in BOTH query and body (defense-in-depth)
  const ids = Array.isArray(options?.ids) ? options.ids.filter(Boolean).map(String) : [];
  if (ids.length) {
    try { params.set('ids', JSON.stringify(ids)); } catch { params.set('ids', ids.join(',')); }
  }

  const url = `/api/eval-runs${params.toString() ? `?${params.toString()}` : ''}`;

  const headers = { 'Content-Type': 'application/json', Accept: 'application/json' };
  const fnKey = import.meta.env.VITE_FUNCTIONS_KEY;
  if (fnKey) headers['x-functions-key'] = fnKey;

  const MAX_CONCURRENCY = 8;
  const rawConc = Number(options.concurrency);
  const safeConcurrency = Math.max(1, Math.min(Number.isFinite(rawConc) ? rawConc : 1, MAX_CONCURRENCY));
  const body = {
    environment: options.environment,
    concurrency: safeConcurrency,
    notes: options.notes,
  // NEW: forward additional options when provided
  ...(typeof options.use_rag === 'boolean' ? { use_rag: options.use_rag } : {}),
  ...(Array.isArray(options.evaluators) ? { evaluators: options.evaluators } : {}),
  ...(options.collection_id ? { collection_id: options.collection_id } : {}),
    ...(ids.length ? { ids } : {}),
  };

  const response = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });

  if (response.status !== 202) {
    const text = await response.text().catch(() => '');
    throw new Error(text || `Failed to create eval run (HTTP ${response.status})`);
  }
  return response.json();
}


